import React, { useState, useEffect, useRef, useCallback } from 'react';
import SlideStage from './SlideStage';
import SlideRenderer from './SlideRenderer';
import useLiveSession from '../../hooks/useLiveSession';
import usePresentation from '../../hooks/usePresentation';
import { claudeCourseSkills } from '../../data/skillLibrary';
import { exportDeckPdf, exportNotesPdf, exportQuickReferenceGuidePdf } from '../../utils/presentationExport';
import './PresentationPlayer.css';

const ACTIVE_SESSION_POLL_MS = 15000;
const NOTE_SAVE_DEBOUNCE_MS = 800;

// Student-facing view, embedded inside LessonPlayer for a lesson of type
// 'presentation'. During class it auto-follows whichever slide the presenter
// is on; once no session is live it becomes a
// normal self-paced deck the learner can revisit any time, with the same
// per-slide notes either way.
export default function PresentationPlayer({ user, presentationId }) {
  const { presentation } = usePresentation(presentationId);
  const stageRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Assigned on every render below, so the listener always sees the current slide.
  const fullscreenKeysRef = useRef(null);
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!(document.fullscreenElement || document.webkitFullscreenElement));
    const onKey = (e) => fullscreenKeysRef.current?.(e);
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
      document.removeEventListener('keydown', onKey);
    };
  }, []);
  const [activeSession, setActiveSession] = useState(undefined); // undefined = checking, null = none
  const [selfIndex, setSelfIndex] = useState(0);
  const [notes, setNotes] = useState({});
  const [noteDraft, setNoteDraft] = useState('');
  const [questionDraft, setQuestionDraft] = useState('');
  const [questionStatus, setQuestionStatus] = useState(null);
  const [submittingQuestion, setSubmittingQuestion] = useState(false);
  const [exporting, setExporting] = useState(null); // 'deck' | 'notes' | 'guide' | null
  const [exportProgress, setExportProgress] = useState(null);
  const saveTimer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    async function check() {
      try {
        const res = await fetch(`/api/presentations/${presentationId}/active-session`);
        const json = await res.json();
        if (!cancelled) setActiveSession(json.success ? json.data : null);
      } catch {
        if (!cancelled) setActiveSession(null);
      }
    }
    check();
    const t = setInterval(check, ACTIVE_SESSION_POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, [presentationId]);

  const liveSessionId = activeSession?.id ?? null;
  const live = useLiveSession(liveSessionId, null);
  const isLive = !!liveSessionId && !live.ended;

  const slides = presentation?.slides || [];
  const manifest = presentation?.manifest;
  const index = isLive ? live.slideIndex : selfIndex;
  const slide = slides[index];

  useEffect(() => {
    if (!user?.id) return;
    fetch(`/api/presentations/${presentationId}/notes?userId=${encodeURIComponent(user.id)}`)
      .then((r) => r.json())
      .then((json) => {
        if (json.success) {
          const map = {};
          json.data.forEach((row) => { map[row.slide_index] = row.note_text; });
          setNotes(map);
        }
      })
      .catch(() => {});
  }, [presentationId, user?.id]);

  useEffect(() => {
    setNoteDraft(notes[index] || '');
  }, [index, notes]);

  const saveNote = useCallback((text) => {
    setNoteDraft(text);
    setNotes((n) => ({ ...n, [index]: text }));
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      fetch(`/api/presentations/${presentationId}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id, slideIndex: index, noteText: text }),
      }).catch(() => {});
    }, NOTE_SAVE_DEBOUNCE_MS);
  }, [index, presentationId, user?.id]);

  const submitQuestion = async (event) => {
    event.preventDefault();
    if (!questionDraft.trim() || !liveSessionId || !user?.id) return;
    setSubmittingQuestion(true);
    setQuestionStatus(null);
    try {
      const response = await fetch(`/api/presentations/${encodeURIComponent(presentationId)}/questions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          sessionId: liveSessionId,
          questionText: questionDraft,
        }),
      });
      const json = await response.json();
      if (json.success) {
        setQuestionDraft('');
        setQuestionStatus({ type: 'success', text: `Sent to the facilitator for slide ${json.data.slide_index + 1}.` });
      } else {
        setQuestionStatus({ type: 'error', text: json.error || 'Your question could not be sent.' });
      }
    } catch {
      setQuestionStatus({ type: 'error', text: 'Your question could not be sent. Check your connection and try again.' });
    } finally {
      setSubmittingQuestion(false);
    }
  };

  if (!presentation) {
    return <div className="pp-missing">This presentation is not available.</div>;
  }

  const goTo = (i) => setSelfIndex(Math.max(0, Math.min(slides.length - 1, i)));
  // The Claude skill downloads and the setup guide are built for the Claude
  // for Professionals deck only.
  const isClaudeDeck = presentationId === 'civils2000-claude-professionals';
  // In full screen, arrow keys, Page Up/Down and Space move between slides.
  fullscreenKeysRef.current = (e) => {
    if (!isFullscreen || isLive) return;
    if (['ArrowRight', 'PageDown', ' '].includes(e.key)) { e.preventDefault(); goTo(index + 1); }
    else if (['ArrowLeft', 'PageUp'].includes(e.key)) { e.preventDefault(); goTo(index - 1); }
  };

  const toggleFullscreen = () => {
    const el = stageRef.current;
    if (!el) return;
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
    } else {
      (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el);
    }
  };

  const runExport = async (kind) => {
    setExporting(kind);
    setExportProgress(null);
    try {
      if (kind === 'deck') {
        await exportDeckPdf(manifest, slides, (done, total) => setExportProgress(`${done} / ${total}`));
      } else if (kind === 'notes') {
        exportNotesPdf(manifest, slides, notes, user?.name);
      } else if (kind === 'guide') {
        exportQuickReferenceGuidePdf(manifest, slides);
      }
    } catch {
      window.alert('That download could not be generated. Please try again.');
    } finally {
      setExporting(null);
      setExportProgress(null);
    }
  };

  return (
    <div className="pp-root">
      <div className="pp-header">
        <div>
          <div className="pp-title">{manifest.title}</div>
          <div className="pp-subtitle">{manifest.subtitle}</div>
        </div>
        {isLive ? (
          <span className="pp-live-badge" role="status">Following live class</span>
        ) : (
          <span className="pp-self-badge">Review mode</span>
        )}
      </div>

      <div className="pp-main">
        <div className={`pp-stage-col${isFullscreen ? ' pp-stage-col--fs' : ''}`} ref={stageRef}>
          <div className="pp-stage-wrap">
            <SlideStage>
              <SlideRenderer key={slide.id} slide={slide} index={index} total={slides.length} manifest={manifest} />
            </SlideStage>
          </div>
          <div className="pp-nav">
            {!isLive && <button type="button" className="btn btn-ghost" onClick={() => goTo(index - 1)} disabled={index === 0}>‹ Back</button>}
            <span className="pp-slide-count" role="status" aria-label={`Slide ${index + 1} of ${slides.length}`}>{index + 1} / {slides.length}</span>
            {!isLive && <button type="button" className="btn btn-ghost" onClick={() => goTo(index + 1)} disabled={index === slides.length - 1}>Next ›</button>}
            <button type="button" className="btn btn-ghost pp-fs-btn" onClick={toggleFullscreen} aria-pressed={isFullscreen}>
              {isFullscreen ? 'Exit full screen' : 'Full screen'}
            </button>
          </div>
        </div>

        <aside className="pp-notes-col">
          {isLive && (
            <form className="pp-question-box" onSubmit={submitQuestion}>
              <div className="pp-question-heading">
                <div>
                  <div className="pp-panel-label">Ask the facilitator</div>
                  <span>Your question will be linked to the slide currently being presented.</span>
                </div>
                <strong>Slide {index + 1}</strong>
              </div>
              <textarea
                className="pp-question-input"
                value={questionDraft}
                onChange={(event) => setQuestionDraft(event.target.value)}
                placeholder="Type your question here"
                rows={3}
                maxLength={2000}
                aria-label="Question for the facilitator"
              />
              <button type="submit" className="btn btn-primary" disabled={!questionDraft.trim() || submittingQuestion}>
                {submittingQuestion ? 'Sending...' : 'Send question'}
              </button>
              {questionStatus && <div className={`pp-question-status ${questionStatus.type}`} role="status">{questionStatus.text}</div>}
            </form>
          )}
          <div className="pp-panel-label">My notes for this slide</div>
          <textarea
            className="pp-notes-input"
            aria-label="My notes for this slide"
            placeholder="Jot down anything worth remembering…"
            value={noteDraft}
            onChange={(e) => saveNote(e.target.value)}
          />
          <div className="pp-notes-hint" role="status">Saved automatically | your notes stay attached to each slide.</div>
        </aside>
      </div>

      <div className="pp-resources">
        <div className="pp-resources-block">
          <div className="pp-panel-label">Downloads</div>
          <div className="pp-download-row">
            <button type="button" className="btn btn-outline" onClick={() => runExport('deck')} disabled={!!exporting}>
              {exporting === 'deck' ? `Preparing… ${exportProgress || ''}` : 'Download full deck (PDF)'}
            </button>
            <button type="button" className="btn btn-outline" onClick={() => runExport('notes')} disabled={!!exporting}>
              {exporting === 'notes' ? 'Preparing…' : 'Download my notes (PDF)'}
            </button>
            {isClaudeDeck && (
              <button type="button" className="btn btn-outline" onClick={() => runExport('guide')} disabled={!!exporting}>
                {exporting === 'guide' ? 'Preparing…' : 'Download setup & quick-reference guide (PDF)'}
              </button>
            )}
          </div>
        </div>

        {isClaudeDeck && (
        <div className="pp-resources-block">
          <div className="pp-panel-label">Claude Skill library</div>
          <div className="pp-skill-list">
            {claudeCourseSkills.map((skill) => (
              <div className="pp-skill-card" key={skill.slug}>
                <div>
                  <div className="pp-skill-name">{skill.name}</div>
                  <div className="pp-skill-desc">{skill.description}</div>
                </div>
                <a className="btn btn-outline" href={skill.zip} download>Download .zip</a>
              </div>
            ))}
          </div>
          <div className="pp-notes-hint">
            To install: unzip and drop the folder into your Claude Skills library (Customize → Skills → Upload), or your local <code>~/.claude/skills/</code> folder.
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
